/**
 * Remembers the open conversation across same-tab navigations.
 *
 * A deep link must not start a new chat. When the host is an SPA, in-place
 * routing keeps this widget mounted. When the host still reloads the page,
 * this store is what puts the same session and messages back on screen.
 */

import type { ResolvedLanguage } from './types';

export interface StoredTurn {
    role: 'user' | 'assistant';
    content: string;
}

export interface StoredConversation {
    sessionId: string;
    language: ResolvedLanguage;
    turns: StoredTurn[];
    suggestions?: string[];
}

function key(appId: string): string {
    return `universal-chatbot:conversation:${appId}`;
}

function storage(): Storage | null {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

export function loadConversation(appId: string): StoredConversation | null {
    const store = storage();
    if (store === null) {
        return null;
    }
    try {
        const raw = store.getItem(key(appId));
        if (raw === null || raw === '') {
            return null;
        }
        const parsed = JSON.parse(raw) as Partial<StoredConversation>;
        if (
            typeof parsed.sessionId !== 'string' ||
            parsed.sessionId === '' ||
            (parsed.language !== 'en' && parsed.language !== 'ar') ||
            !Array.isArray(parsed.turns)
        ) {
            return null;
        }
        const turns = parsed.turns.filter(
            (turn): turn is StoredTurn =>
                turn !== null &&
                typeof turn === 'object' &&
                (turn.role === 'user' || turn.role === 'assistant') &&
                typeof turn.content === 'string'
        );
        const suggestions = Array.isArray(parsed.suggestions)
            ? parsed.suggestions.filter(
                  (item): item is string =>
                      typeof item === 'string' && item.trim() !== ''
              )
            : [];
        return {
            sessionId: parsed.sessionId,
            language: parsed.language,
            turns,
            suggestions
        };
    } catch {
        return null;
    }
}

export function saveConversation(appId: string, data: StoredConversation): void {
    const store = storage();
    if (store === null) {
        return;
    }
    try {
        store.setItem(key(appId), JSON.stringify(data));
    } catch {
        // Quota or private-mode failures must not break chat.
    }
}

export function clearConversation(appId: string): void {
    const store = storage();
    if (store === null) {
        return;
    }
    try {
        store.removeItem(key(appId));
    } catch {
        // Ignore.
    }
}
